import React, { useState, useMemo, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { registerUser, resendVerificationEmail } from "../services/api";

// Constantes pour la validation
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MIN_STRENGTH = 40;

function Register() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [resendingEmail, setResendingEmail] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Vérifie si on vient d'une redirection après une erreur Google OAuth
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("error") === "google_auth_failed") {
      toast.error(
        "La connexion avec Google a échoué. Veuillez réessayer ou utiliser une autre méthode."
      );
    }
  }, [location]);

  // Fonction pour se connecter avec Google
  const handleGoogleSignIn = () => {
    setIsGoogleLoading(true);
    // Redirection vers l'endpoint de l'API pour l'authentification Google
    window.location.href = "/api/auth/google";
  };

  // Fonction pour renvoyer l'email de vérification
  const handleResendEmail = async () => {
    if (!registeredEmail) return;

    setResendingEmail(true);
    try {
      await resendVerificationEmail(registeredEmail);
      toast.success("Un nouvel email de vérification a été envoyé.");
    } catch (error) {
      console.error("Erreur lors du renvoi de l'email:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Erreur lors de l'envoi de l'email. Veuillez réessayer.";
      toast.error(errorMessage);
    } finally {
      setResendingEmail(false);
    }
  };

  // Fonction pour analyser la force du mot de passe (optimisée avec useMemo)
  const passwordStrength = useMemo(() => {
    const { password } = formData;
    if (!password) return { score: 0, text: "", className: "" };

    // Calcule le score sur 100
    let score = 0;
    let feedback = [];

    // Critères de base (longueur)
    if (password.length >= PASSWORD_MIN_LENGTH) {
      score += 20;
    } else {
      feedback.push("trop court");
    }

    // Contient des chiffres
    if (/\d/.test(password)) {
      score += 20;
    } else {
      feedback.push("ajoutez des chiffres");
    }

    // Contient des minuscules
    if (/[a-z]/.test(password)) {
      score += 20;
    } else {
      feedback.push("ajoutez des lettres minuscules");
    }

    // Contient des majuscules
    if (/[A-Z]/.test(password)) {
      score += 20;
    } else {
      feedback.push("ajoutez des lettres majuscules");
    }

    // Contient des caractères spéciaux
    if (/[^a-zA-Z0-9]/.test(password)) {
      score += 20;
    } else {
      feedback.push("ajoutez des caractères spéciaux");
    }

    // Déterminer le texte et la classe en fonction du score
    let text, className;
    if (score >= 80) {
      text = "Fort";
      className = "bg-success";
    } else if (score >= 60) {
      text = "Bon";
      className = "bg-info";
    } else if (score >= 40) {
      text = "Moyen";
      className = "bg-warning";
    } else {
      text = "Faible";
      className = "bg-danger";
    }

    return {
      score,
      text,
      className,
      feedback: feedback.join(", "),
    };
  }, [formData.password]);

  // Validation des champs (optimisée avec useMemo)
  const formValidation = useMemo(() => {
    const newErrors = {};

    // Validation du mot de passe
    if (formData.password && formData.password.length < PASSWORD_MIN_LENGTH) {
      newErrors.password = `Le mot de passe doit contenir au moins ${PASSWORD_MIN_LENGTH} caractères`;
    }

    // Validation de la correspondance des mots de passe
    if (
      formData.password &&
      formData.confirmPassword &&
      formData.password !== formData.confirmPassword
    ) {
      newErrors.confirmPassword = "Les mots de passe ne correspondent pas";
    }

    // Validation de l'email
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Format d'email invalide";
    }

    return newErrors;
  }, [formData.password, formData.confirmPassword, formData.email]);

  // Mettez à jour errors quand formValidation change
  useEffect(() => {
    setErrors(formValidation);
  }, [formValidation]);

  // Gestionnaire de changement
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Gestionnaire pour afficher/masquer le mot de passe
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Gestionnaire pour afficher/masquer la confirmation du mot de passe
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Gestionnaire de soumission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Vérification des erreurs de validation
    if (Object.keys(formValidation).length > 0) {
      // Afficher les erreurs de validation
      Object.values(formValidation).forEach((error) => toast.error(error));
      return;
    }

    // Vérification de la force du mot de passe
    if (passwordStrength.score < PASSWORD_MIN_STRENGTH) {
      toast.warning(
        "Votre mot de passe est trop faible. Essayez de l'améliorer."
      );
      return;
    }

    setIsLoading(true);

    try {
      await registerUser(
        formData.firstName,
        formData.lastName,
        formData.email,
        formData.password
      );

      setRegistrationSuccess(true);
      setRegisteredEmail(formData.email);

      // Réinitialiser le formulaire
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
      });

      // Ne pas rediriger mais afficher le message de vérification d'email
      toast.success(
        "Votre compte a été créé avec succès ! Veuillez vérifier votre email pour l'activer."
      );
    } catch (error) {
      console.error("Erreur d'inscription:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Erreur lors de l'inscription. Veuillez réessayer.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Afficher l'écran de confirmation si l'inscription est réussie
  if (registrationSuccess) {
    return (
      <div className="container d-flex align-items-center justify-content-center min-vh-100">
        <div
          className="card shadow-lg border-0 rounded-3"
          style={{ maxWidth: "500px", width: "100%" }}
        >
          <div className="card-body p-4 p-lg-5 text-center">
            <i className="bi bi-envelope-check-fill fs-1 text-success mb-3"></i>
            <h2>Vérifiez votre email</h2>
            <p className="mb-4">
              Nous avons envoyé un email de vérification à{" "}
              <strong>{registeredEmail}</strong>. Veuillez cliquer sur le lien
              dans cet email pour activer votre compte.
            </p>
            <p className="text-muted mb-4">
              Si vous ne recevez pas l'email dans les prochaines minutes,
              veuillez vérifier votre dossier spam.
            </p>
            <div className="d-grid gap-3">
              <button
                className="btn btn-outline-primary"
                onClick={handleResendEmail}
                disabled={resendingEmail}
              >
                {resendingEmail ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <i className="bi bi-arrow-repeat me-2"></i>
                    Renvoyer l'email de vérification
                  </>
                )}
              </button>
              <Link to="/login" className="btn btn-primary">
                <i className="bi bi-box-arrow-in-right me-2"></i>
                Aller à la page de connexion
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container d-flex align-items-center justify-content-center min-vh-100">
      <div
        className="card shadow-lg border-0 rounded-3"
        style={{ maxWidth: "500px", width: "100%" }}
      >
        <div className="card-body p-4 p-lg-5">
          <div className="text-center mb-4">
            <i className="bi bi-person-plus-fill fs-1 text-primary"></i>
            <h2 className="card-title mt-3">Inscription</h2>
            <p className="text-muted">
              Créez votre compte pour commencer l'aventure
            </p>
          </div>

          {/* Bouton de connexion Google */}
          <div className="d-grid mb-4">
            <button
              type="button"
              className="btn btn-light position-relative border d-flex align-items-center justify-content-center gap-3 py-3 shadow-sm hover-shadow"
              onClick={handleGoogleSignIn}
              disabled={isGoogleLoading}
              style={{ transition: "all 0.3s ease" }}
            >
              {isGoogleLoading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="24"
                  viewBox="0 0 24 24"
                  width="24"
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                  <path d="M1 1h22v22H1z" fill="none" />
                </svg>
              )}
              <span className="fw-medium fs-5">
                {isGoogleLoading
                  ? "Inscription en cours..."
                  : "S'inscrire avec Google"}
              </span>
            </button>
          </div>

          <div className="text-center mb-4">
            <div className="divider d-flex align-items-center justify-content-center">
              <hr className="flex-grow-1" />
              <span className="text-muted mx-3">ou créez un compte</span>
              <hr className="flex-grow-1" />
            </div>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Prénom et Nom sur la même ligne */}
            <div className="row mb-3">
              <div className="col-md-6 mb-3 mb-md-0">
                <label htmlFor="firstName" className="form-label fw-semibold">
                  Prénom
                </label>
                <div className="input-group">
                  <span className="input-group-text bg-light">
                    <i className="bi bi-person-fill text-primary"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    id="firstName"
                    name="firstName"
                    placeholder="Votre prénom"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="col-md-6">
                <label htmlFor="lastName" className="form-label fw-semibold">
                  Nom
                </label>
                <div className="input-group">
                  <span className="input-group-text bg-light">
                    <i className="bi bi-person-fill text-primary"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    id="lastName"
                    name="lastName"
                    placeholder="Votre nom"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="mb-3">
              <label htmlFor="email" className="form-label fw-semibold">
                Adresse email
              </label>
              <div className="input-group input-group-lg">
                <span className="input-group-text bg-light" id="emailIcon">
                  <i className="bi bi-envelope-fill text-primary"></i>
                </span>
                <input
                  type="email"
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  id="email"
                  name="email"
                  placeholder="Votre email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                {errors.email && (
                  <div className="invalid-feedback">{errors.email}</div>
                )}
              </div>
            </div>

            {/* Mot de passe */}
            <div className="mb-3">
              <label htmlFor="password" className="form-label fw-semibold">
                Mot de passe
              </label>
              <div className="input-group input-group-lg">
                <span className="input-group-text bg-light" id="passwordIcon">
                  <i className="bi bi-lock-fill text-primary"></i>
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  className={`form-control ${
                    errors.password ? "is-invalid" : ""
                  }`}
                  id="password"
                  name="password"
                  placeholder="Choisissez un mot de passe"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <button
                  className="input-group-text bg-light"
                  type="button"
                  onClick={togglePasswordVisibility}
                  aria-label={
                    showPassword
                      ? "Masquer le mot de passe"
                      : "Afficher le mot de passe"
                  }
                >
                  <i
                    className={`bi ${
                      showPassword ? "bi-eye-slash-fill" : "bi-eye-fill"
                    } text-secondary`}
                  ></i>
                </button>
                {errors.password && (
                  <div className="invalid-feedback">{errors.password}</div>
                )}
              </div>

              {/* Jauge de confiance améliorée pour le mot de passe */}
              {formData.password && (
                <div className="mt-2">
                  <div className="progress" style={{ height: "10px" }}>
                    <div
                      className={`progress-bar ${passwordStrength.className}`}
                      role="progressbar"
                      style={{ width: `${passwordStrength.score}%` }}
                      aria-valuenow={passwordStrength.score}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <small>
                      Force:{" "}
                      <span
                        className={`fw-bold text-${passwordStrength.className.replace(
                          "bg-",
                          ""
                        )}`}
                      >
                        {passwordStrength.text}
                      </span>
                    </small>
                    {passwordStrength.feedback && (
                      <small className="text-muted">
                        <i className="bi bi-info-circle me-1"></i>
                        {passwordStrength.feedback}
                      </small>
                    )}
                  </div>

                  {/* Indicateurs visuels des critères */}
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    <span
                      className={`badge ${
                        /[a-z]/.test(formData.password)
                          ? "bg-success"
                          : "bg-secondary"
                      }`}
                    >
                      <i
                        className={`bi ${
                          /[a-z]/.test(formData.password) ? "bi-check" : "bi-x"
                        } me-1`}
                      ></i>
                      Minuscules
                    </span>
                    <span
                      className={`badge ${
                        /[A-Z]/.test(formData.password)
                          ? "bg-success"
                          : "bg-secondary"
                      }`}
                    >
                      <i
                        className={`bi ${
                          /[A-Z]/.test(formData.password) ? "bi-check" : "bi-x"
                        } me-1`}
                      ></i>
                      Majuscules
                    </span>
                    <span
                      className={`badge ${
                        /\d/.test(formData.password)
                          ? "bg-success"
                          : "bg-secondary"
                      }`}
                    >
                      <i
                        className={`bi ${
                          /\d/.test(formData.password) ? "bi-check" : "bi-x"
                        } me-1`}
                      ></i>
                      Chiffres
                    </span>
                    <span
                      className={`badge ${
                        /[^a-zA-Z0-9]/.test(formData.password)
                          ? "bg-success"
                          : "bg-secondary"
                      }`}
                    >
                      <i
                        className={`bi ${
                          /[^a-zA-Z0-9]/.test(formData.password)
                            ? "bi-check"
                            : "bi-x"
                        } me-1`}
                      ></i>
                      Spéciaux
                    </span>
                    <span
                      className={`badge ${
                        formData.password.length >= PASSWORD_MIN_LENGTH
                          ? "bg-success"
                          : "bg-secondary"
                      }`}
                    >
                      <i
                        className={`bi ${
                          formData.password.length >= PASSWORD_MIN_LENGTH
                            ? "bi-check"
                            : "bi-x"
                        } me-1`}
                      ></i>
                      {PASSWORD_MIN_LENGTH}+ caractères
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Confirmation du mot de passe */}
            <div className="mb-4">
              <label
                htmlFor="confirmPassword"
                className="form-label fw-semibold"
              >
                Confirmez le mot de passe
              </label>
              <div className="input-group input-group-lg">
                <span className="input-group-text bg-light">
                  <i className="bi bi-lock-fill text-primary"></i>
                </span>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className={`form-control ${
                    errors.confirmPassword ? "is-invalid" : ""
                  }`}
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="Confirmez votre mot de passe"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
                <button
                  className="input-group-text bg-light"
                  type="button"
                  onClick={toggleConfirmPasswordVisibility}
                  aria-label={
                    showConfirmPassword
                      ? "Masquer la confirmation du mot de passe"
                      : "Afficher la confirmation du mot de passe"
                  }
                >
                  <i
                    className={`bi ${
                      showConfirmPassword ? "bi-eye-slash-fill" : "bi-eye-fill"
                    } text-secondary`}
                  ></i>
                </button>
                {errors.confirmPassword && (
                  <div className="invalid-feedback">
                    {errors.confirmPassword}
                  </div>
                )}
              </div>
            </div>

            {/* Bouton d'inscription */}
            <button
              type="submit"
              className="btn btn-primary btn-lg w-100 fw-bold d-flex align-items-center justify-content-center py-3 mb-4"
              disabled={isLoading || Object.keys(formValidation).length > 0}
              style={{ transition: "all 0.3s ease" }}
            >
              {isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Chargement...
                </>
              ) : (
                <>
                  <i className="bi bi-person-plus me-2"></i>
                  S'inscrire
                </>
              )}
            </button>

            <div className="text-center">
              <p className="mb-0">
                Vous avez déjà un compte ?{" "}
                <Link to="/login" className="text-decoration-none fw-semibold">
                  Connectez-vous
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Register;
